import {
  createApi,
  fetchBaseQuery,
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/query/react";
import { RootState } from ".";
import { logout } from "./slices/authSlice";
import {
  Order,
  OrdersResponse,
  AppealsResponse,
  Profile,
  Balance,
  GetOrdersParams,
  GetAppealsParams,
  CreateAppealRequest,
  FileUploadResponse,
  WithdrawalsResponse,
  GetWithdrawalsParams,
  CreateWithdrawalRequest,
  WithdrawalRequest,
  OrderStats,
  WithdrawalStats,
  DashboardStats,
  RevenueStats,
} from "../types/api";

// Создаем базовый запрос с обработкой ошибок
const baseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_API_URL || "http://localhost:3001/api",
  prepareHeaders: (headers, { getState, endpoint }) => {
    const token = (getState() as RootState).auth.token;
    if (token) {
      headers.set("authorization", `Bearer ${token}`);
    }

    // Don't set Content-Type for FormData uploads (appeals/upload, receipt uploads, merchant respond)
    const isFileUpload =
      endpoint?.includes("upload") || endpoint?.includes("receipt") || endpoint?.includes("Respond");

    if (!isFileUpload && !headers.has("Content-Type")) {
      headers.set("Content-Type", "application/json");
    }

    headers.set("Accept", "application/json");

    const headerSize = JSON.stringify(
      Object.fromEntries(headers.entries())
    ).length;

    if (headerSize > 16384) {
      console.warn(
        `⚠️ Large headers detected in merchant: ${headerSize} bytes - token size: ${token?.length || 0}`
      );

      if (headerSize > 32768) {
        console.error(
          `❌ Headers too large in merchant: ${headerSize} bytes - clearing storage`
        );
        localStorage.clear();
        sessionStorage.clear();
        window.location.reload();
      }
    }

    return headers;
  },
  validateStatus: (response) => {
    if (response.status === 431) {
      console.error("❌ Error 431: Request Header Fields Too Large");
      console.log("🧹 Clearing all storage due to 431 error...");
      localStorage.clear();
      sessionStorage.clear();

      setTimeout(() => {
        window.location.href = "/login";
      }, 100);
      return false;
    }

    return response.status >= 200 && response.status < 300;
  },
  fetchFn: async (...args) => {
    try {
      return await fetch(...args);
    } catch (error) {
      console.error("❌ Merchant Fetch error:", error);
      throw error;
    }
  },
});

// Добавляем обработку 401 ошибок
const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  let result = await baseQuery(args, api, extraOptions);

  if (result.error && result.error.status === 401) {
    console.log("401 Unauthorized - logging out");
    api.dispatch(logout());
    window.location.href = "/login";
  }

  return result;
};

export const api = createApi({
  reducerPath: "api",
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    "User",
    "Order",
    "Balance",
    "Settings",
    "Appeals",
    "Withdrawals",
    "Analytics",
    "ExchangeRate",
  ],
  endpoints: (builder) => ({
    login: builder.mutation<
      { user?: any; token?: string; refreshToken?: string; requiresTwoFactor?: boolean; tempToken?: string },
      { login: string; password: string }
    >({
      query: (credentials) => ({
        url: "/auth/login?requiredRole=MERCHANT",
        method: "POST",
        body: credentials,
      }),
    }),

    refreshToken: builder.mutation<
      { token: string; refreshToken: string },
      { refreshToken: string }
    >({
      query: (body) => ({
        url: "/auth/refresh",
        method: "POST",
        body,
      }),
    }),

    getProfile: builder.query<Profile, void>({
      query: () => "/auth/profile",
      providesTags: ["User"],
    }),

    // 2FA endpoints
    setup2FA: builder.mutation<
      { secret: string; qrCode: string; otpauthUrl: string },
      void
    >({
      query: () => ({
        url: "/auth/2fa/setup",
        method: "POST",
      }),
    }),

    verify2FASetup: builder.mutation<
      { message: string; backupCodes: string[] },
      { code: string }
    >({
      query: (body) => ({
        url: "/auth/2fa/verify-setup",
        method: "POST",
        body,
      }),
    }),

    validate2FA: builder.mutation<
      { token: string; refreshToken: string; user: any },
      { tempToken: string; code: string }
    >({
      query: (body) => ({
        url: "/auth/2fa/validate",
        method: "POST",
        body,
      }),
    }),

    disable2FA: builder.mutation<
      { message: string },
      { password: string; code: string }
    >({
      query: (body) => ({
        url: "/auth/2fa/disable",
        method: "POST",
        body,
      }),
    }),

    regenerateBackupCodes: builder.mutation<
      { backupCodes: string[] },
      { code: string }
    >({
      query: (body) => ({
        url: "/auth/2fa/backup-codes",
        method: "POST",
        body,
      }),
    }),

    changePassword: builder.mutation<
      { message: string },
      { currentPassword: string; newPassword: string; confirmPassword: string }
    >({
      query: (body) => ({
        url: "/auth/change-password",
        method: "POST",
        body,
      }),
    }),

    get2FAStatus: builder.query<
      { enabled: boolean; backupCodesRemaining: number },
      void
    >({
      query: () => "/auth/2fa/status",
      providesTags: ["User"],
    }),

    getOrders: builder.query<OrdersResponse, GetOrdersParams>({
      query: (params) => ({
        url: "/orders",
        params,
      }),
      providesTags: ["Order"],
    }),
    getOrderById: builder.query<Order, string>({
      query: (id) => `/orders/${id}`,
      providesTags: (result, error, id) => [{ type: "Order", id }],
    }),

    getOrder: builder.query<Order, string>({
      query: (id) => `/orders/${id}`,
      providesTags: (result, error, id) => [{ type: "Order", id }],
    }),

    createOrder: builder.mutation<any, any>({
      query: (orderData) => ({
        url: "/orders/create",
        method: "POST",
        body: orderData,
      }),
      invalidatesTags: ["Order"],
    }),

    getBalance: builder.query<Balance, void>({
      query: () => "/balance",
      providesTags: ["Balance"],
    }),

    depositBalance: builder.mutation<any, { amount: number; method: string }>({
      query: (data) => ({
        url: "/balance/deposit",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Balance"],
    }),

    withdrawBalance: builder.mutation<
      any,
      { amount: number; walletAddress: string }
    >({
      query: (data) => ({
        url: "/balance/withdraw",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Balance"],
    }),

    getBalanceHistory: builder.query<any[], { page?: number; limit?: number }>({
      query: (params) => ({
        url: "/balance/history",
        params,
      }),
      providesTags: ["Balance"],
    }),

    getSettings: builder.query<any, void>({
      query: () => "/settings",
      providesTags: ["Settings"],
    }),

    updateSettings: builder.mutation<any, any>({
      query: (data) => ({
        url: "/settings",
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["Settings"],
    }),

    updateProfile: builder.mutation<any, any>({
      query: (data) => ({
        url: "/merchants/profile",
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["User", "Settings"],
    }),

    regenerateWebhookSecret: builder.mutation<{ callbackSecret: string }, void>({
      query: () => ({
        url: "/merchants/regenerate-webhook-secret",
        method: "POST",
      }),
      invalidatesTags: ["User", "Settings"],
    }),

    generateApiKey: builder.mutation<any, void>({
      query: () => ({
        url: "/auth/generate-api-key",
        method: "POST",
      }),
      invalidatesTags: ["User"],
    }),

    // Appeals endpoints
    getAppeals: builder.query<AppealsResponse, GetAppealsParams>({
      query: ({ page = 1, limit = 10, status, orderId }) => {
        const params = new URLSearchParams({
          page: page.toString(),
          limit: limit.toString(),
        });
        if (status) params.append("status", status);
        if (orderId) params.append("orderId", orderId);

        return `appeals?${params}`;
      },
      providesTags: ["Appeals"],
    }),

    getAppeal: builder.query<any, string>({
      query: (id) => `appeals/${id}`,
      providesTags: (_result, _error, id) => [{ type: "Appeals", id }],
    }),

    uploadAppealFiles: builder.mutation<FileUploadResponse, File[]>({
      query: (files) => {
        const formData = new FormData();
        files.forEach((file) => formData.append("files", file));
        return {
          url: "appeals/upload",
          method: "POST",
          body: formData,
          // Do not set Content-Type here; browser will set correct multipart boundary
        } as any;
      },
    }),

    createAppeal: builder.mutation<any, CreateAppealRequest>({
      query: (data) => ({
        url: "appeals",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Appeals"],
    }),

    merchantRespondToAppeal: builder.mutation<any, { id: string; files: File[]; comment: string }>({
      query: ({ id, files, comment }) => {
        const formData = new FormData();
        files.forEach((file) => formData.append("files", file));
        formData.append("comment", comment);
        return {
          url: `appeals/${id}/merchant-respond`,
          method: "POST",
          body: formData,
        } as any;
      },
      invalidatesTags: ["Appeals"],
    }),

    // Withdrawals endpoints
    getWithdrawals: builder.query<WithdrawalsResponse, GetWithdrawalsParams>({
      query: ({ page = 1, limit = 10, status }) => {
        const params = new URLSearchParams({
          page: page.toString(),
          limit: limit.toString(),
        });
        if (status) params.append("status", status);

        return `withdrawals?${params}`;
      },
      providesTags: ["Withdrawals"],
    }),

    getWithdrawal: builder.query<WithdrawalRequest, string>({
      query: (id) => `withdrawals/${id}`,
      providesTags: (_result, _error, id) => [{ type: "Withdrawals", id }],
    }),

    createWithdrawal: builder.mutation<
      WithdrawalRequest,
      CreateWithdrawalRequest
    >({
      query: (data) => ({
        url: "withdrawals",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Withdrawals", "Balance"],
    }),

    getWithdrawalStats: builder.query<WithdrawalStats, void>({
      query: () => "withdrawals/stats",
      providesTags: ["Withdrawals"],
    }),

    // Analytics/Reports endpoints
    getOrderStats: builder.query<OrderStats, void>({
      query: () => "orders/stats/overview",
      providesTags: ["Analytics"],
    }),

    getDashboardStats: builder.query<DashboardStats, { period?: string }>({
      query: ({ period = "30d" }) => ({
        url: "analytics/dashboard",
        params: { period },
      }),
      providesTags: ["Analytics"],
    }),

    getRevenueStats: builder.query<
      RevenueStats,
      { period?: string; groupBy?: string }
    >({
      query: ({ period = "30d", groupBy = "day" }) => ({
        url: "analytics/revenue",
        params: { period, groupBy },
      }),
      providesTags: ["Analytics"],
    }),

    // Exchange rates endpoints
    getExchangeRate: builder.query<
      {
        source: string;
        pair: string;
        rate: number;
        markup: string;
        timestamp: string;
      },
      void
    >({
      query: () => "/exchange-rates/rapira/usdt-rub",
      providesTags: ["ExchangeRate"],
      keepUnusedDataFor: 0, // Не кэшируем данные
    }),

    // Tariffs endpoints
    getMerchantTariffs: builder.query<any, string>({
      query: (merchantId) => `/tariffs/merchant/${merchantId}`,
      providesTags: ["Settings"],
    }),

    // Receipt upload endpoint
    uploadOrderReceipt: builder.mutation<
      { message: string; receipt: any },
      { orderId: string; file: File; type: string }
    >({
      query: ({ orderId, file, type }) => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("type", type);
        return {
          url: `orders/${orderId}/receipt`,
          method: "POST",
          body: formData,
        } as any;
      },
      invalidatesTags: (result, error, { orderId }) => [
        { type: "Order", id: orderId },
      ],
    }),

    // Receipt download endpoint
    downloadOrderReceipt: builder.query<
      Blob,
      { orderId: string; type: string; filename: string }
    >({
      query: ({ orderId, type, filename }) => ({
        url: `orders/${orderId}/receipt/${type}/download/${filename}`,
        responseHandler: (response) => response.blob(),
      }),
    }),
  }),
});

export const {
  useLoginMutation,
  useRefreshTokenMutation,
  useGetProfileQuery,
  useGetOrdersQuery,
  useGetOrderQuery,
  useCreateOrderMutation,
  useGetOrderByIdQuery,
  useGetBalanceQuery,
  useDepositBalanceMutation,
  useWithdrawBalanceMutation,
  useGetBalanceHistoryQuery,
  useGetSettingsQuery,
  useUpdateSettingsMutation,
  useUpdateProfileMutation,
  useRegenerateWebhookSecretMutation,
  useGenerateApiKeyMutation,

  // 2FA hooks
  useSetup2FAMutation,
  useVerify2FASetupMutation,
  useValidate2FAMutation,
  useDisable2FAMutation,
  useRegenerateBackupCodesMutation,
  useChangePasswordMutation,
  useGet2FAStatusQuery,

  // Appeals hooks
  useGetAppealsQuery,
  useGetAppealQuery,
  useUploadAppealFilesMutation,
  useCreateAppealMutation,
  useMerchantRespondToAppealMutation,

  // Withdrawals hooks
  useGetWithdrawalsQuery,
  useGetWithdrawalQuery,
  useCreateWithdrawalMutation,
  useGetWithdrawalStatsQuery,

  // Analytics/Reports hooks
  useGetOrderStatsQuery,
  useGetDashboardStatsQuery,
  useGetRevenueStatsQuery,

  // Exchange rate hooks
  useGetExchangeRateQuery,

  // Tariffs hooks
  useGetMerchantTariffsQuery,

  // Receipt upload hooks
  useUploadOrderReceiptMutation,
  useDownloadOrderReceiptQuery,
} = api;
